// 打印一个html dom元素
import { promiseTimeout as sleep } from '@vueuse/core';

// 掠过的style
const skipStyles = new Set([
  'cursor',
  'caret-color',
  'block-size',
  'user-select',
  'border-block-start-color',
  'border-block-end-color',
  'transform-origin',
  '-webkit-locale'
]);

function collectStyles(
  element: HTMLElement,
  window: Window,
  tagDefaultStyleMap: Record<string, Record<string, string>>
) {
  const defaultStyleMap = tagDefaultStyleMap[element.tagName];

  // Loop over computed styles
  const styles = window.getComputedStyle(element, '');

  const styleArray: string[] = [];
  for (let i = 0; i < styles.length; i++) {
    const key = styles[i];
    const value = styles.getPropertyValue(key);

    if (skipStyles.has(key)) {
      continue;
    }

    if (defaultStyleMap) {
      if (value !== defaultStyleMap[key]) {
        styleArray.push(key, ':', value, ';');
      }
    } else {
      styleArray.push(key, ':', value, ';');
    }
  }

  return styleArray.join('');
}

function cloneElement(
  element: HTMLElement,
  window: Window,
  tagDefaultStyleMap: Record<string, Record<string, string>>
) {
  // Clone the main node (if not already inside the recursion process)
  const clone = element.cloneNode() as HTMLElement;

  // Loop over and process the children elements / nodes (including text nodes)
  const childNodesArray = Array.prototype.slice.call(element.childNodes);
  for (let i = 0; i < childNodesArray.length; i++) {
    // Check if we are skipping the current element
    // if (params.ignoreElements.indexOf(childNodesArray[i].id) !== -1) {
    //   continue;
    // }

    // Clone the child element
    const clonedChild = cloneElement(
      childNodesArray[i],
      window,
      tagDefaultStyleMap
    );

    // Attach the cloned child to the cloned parent node
    clone.appendChild(clonedChild);
  }

  // Get all styling for print element (for nodes of type element only)
  if (element.nodeType === 1) {
    clone.setAttribute(
      'style',
      collectStyles(element, window, tagDefaultStyleMap)
    );
  }

  // Check if the element needs any state processing (copy user input data)
  switch (element.tagName) {
    case 'SELECT':
      // Copy the current selection value to its clone
      (clone as HTMLSelectElement).value = (element as HTMLSelectElement).value;
      break;
    case 'CANVAS':
      // Copy the canvas content to its clone
      (clone as HTMLCanvasElement)
        .getContext('2d')
        ?.drawImage(element as HTMLCanvasElement, 0, 0);
      break;
  }

  return clone;
}

function loadIframeImages(images: HTMLImageElement[]) {
  const promises = images.map((image) => {
    if (image.src && image.src !== window.location.href) {
      return loadIframeImage(image);
    }
  });

  return Promise.all(promises);
}

function loadIframeImage(image: HTMLImageElement) {
  return new Promise((resolve) => {
    const pollImage = () => {
      !image ||
      typeof image.naturalWidth === 'undefined' ||
      image.naturalWidth === 0 ||
      !image.complete
        ? setTimeout(pollImage, 500)
        : resolve(true);
    };
    pollImage();
  });
}

/*
function getTagDefaultStyles(
  printFrame: HTMLIFrameElement,
  tag: 'span' | 'div'
) {
  const frameDocument = (printFrame.contentWindow?.document ||
    printFrame.contentDocument)!;

  const dummy = frameDocument.createElement(tag);
  frameDocument.body.appendChild(dummy);

  const defaultStyles = printFrame.contentWindow!.getComputedStyle(dummy)!;

  const defaultStyleMap: Record<string, string> = {};

  for (let i = 0; i < defaultStyles.length; i++) {
    const styleValue = defaultStyles.getPropertyValue(defaultStyles[i]);

    defaultStyleMap[defaultStyles[i]] = styleValue;
  }

  frameDocument.body.removeChild(dummy);

  return defaultStyleMap;
}*/

/**
 * 打印 dom元素
 * @param element
 * @param options
 */
export async function printDom(
  element: HTMLElement,
  options?: { title?: string; loaded?: () => Promise<void>; styles?: string }
) {
  // Create a new iframe for the print job
  let printFrame = document.createElement('iframe');

  // const win = document.defaultView || window;
  // const styles = win.getComputedStyle(element, '');

  // const width = parseInt(styles.width) + 40 + 'px';
  // const height = parseInt(styles.height) + 40 + 'px';

  printFrame.setAttribute(
    'style',
    'visibility: hidden; height: 0; width: 0; position: absolute; border: 0;'
  );

  // printFrame.setAttribute(
  //   'style',
  //   'z-index:10000;position:fixed;left:0;top:0;width:100%;height:100%'
  // );

  const frameContents = ['<html><head><title>', options?.title, '</title>'];

  // ag-grid css
  frameContents.push('<style type="text/css">');
  frameContents.push('.ag-root-wrapper {display: flex!important;}');
  if (options?.styles) frameContents.push(options.styles);
  frameContents.push('</style>');

  frameContents.push('</head><body>');
  //frameContents.push('<button onclick="print()">打印</button>');
  frameContents.push('</body></html>');

  // Append iframe element to document body
  printFrame = document.body.appendChild(printFrame);

  const frameDocument = (printFrame.contentWindow?.document ||
    printFrame.contentDocument)!;

  frameDocument.open();
  frameDocument.write(frameContents.join(''));
  frameDocument.close();

  const tagDefaultStyleMap = {
    //SPAN: getTagDefaultStyles(printFrame, 'span'),
    //DIV: getTagDefaultStyles(printFrame, 'div')
  };

  const printableElement = cloneElement(
    element,
    printFrame.contentWindow!,
    tagDefaultStyleMap
  );

  // Append printable element to the iframe body
  frameDocument.body.appendChild(printableElement);

  // If printing images, wait for them to load inside the iframe
  const images = frameDocument.getElementsByTagName('img');

  if (images.length > 0) {
    try {
      await loadIframeImages(Array.from(images));
    } catch (e) {
      console.warn('load image fail', e);
    }
  } else {
    await sleep(1000);
  }

  printFrame.focus();
  await sleep(100);

  // 通知准备完成
  if (options?.loaded) {
    await options.loaded();
  }

  printFrame.contentWindow!.print();

  document.body.removeChild(printFrame);
}
